import React from 'react';
import { createSelector } from 'reselect';
import get from 'lodash/get';

import {
  dataSelector,
  metricSelector,
  filtersTableDataSortSelector,
} from '@store/noc/filtersData/selectors';
import { initThresholdSelector, isThresholdDisabledSelector } from '@store/noc/threshold/selectors';
import { autoSortEnabledSelector } from '@store/noc/base/selectors';
import { getMetricMetaByKey } from '@src/utils/metrics/metricHelpers';
import { Cell, Scope, Header } from '../common';
import { thresholdBaselineSelector, calcRowThreshold, autoSort } from '../common/utils';
import { ScopeWrapper, RightDiv } from '../styled';
import { currentMetricsMetaSelector } from '@store/common/selectors';
import { DeletedMetricWithRemove } from '@components/common/DeletedMetric';
export const columnsSelector = createSelector(
  dataSelector,
  metricSelector,
  currentMetricsMetaSelector,
  (_, errorData: any) => errorData,
  (_, __, deleteMetric: any) => deleteMetric,
  (_1, _2, _3, invalidDimensionCheck: any) => invalidDimensionCheck,
  (
    res,
    metrics: [],
    allMetrics: any,
    errorData: any,
    deleteMetric: (value) => void,
    invalidDimensionCheck: (...p: any[]) => React.ReactNode,
  ) => {
    const { maxData } = res as any;

    const columns: Record<string, any>[] = [
      {
        title: '',
        dataKey: 'filter',
        key: 'filter.name',
        width: 400,
        frozen: true,
        resizable: true,
        cellRenderer: ({ cellData, rowData }) => {
          return (
            <ScopeWrapper>
              <Scope text={cellData.name} rowData={rowData} />
              {invalidDimensionCheck(cellData.rule, {
                style: {
                  fontSize: 18,
                  position: 'absolute',
                  right: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                },
              })}
            </ScopeWrapper>
          );
        },
        align: 'left',
      },
    ];

    metrics.forEach((metric) => {
      const isExist = !!allMetrics[metric] && !errorData[metric];
      const metricInfo = getMetricMetaByKey({ metricKey: metric });
      const max = maxData[metric];
      const column = {
        // title: metricInfo.displayName,
        dataKey: metric,
        key: metric,
        width: 180,
        style: { overflow: 'auto' },
        maxData: max,
        headerRenderer: () =>
          !isExist ? (
            <DeletedMetricWithRemove
              onRemove={() => {
                deleteMetric(metric);
              }}
            />
          ) : (
            <Header metric={metricInfo} />
          ),
        cellRenderer: ({ cellData, column, rowData }) => {
          return !isExist ? (
            <RightDiv> - </RightDiv>
          ) : (
            <Cell
              cellData={cellData}
              column={column}
              rowData={rowData}
              unit={metricInfo.unit}
              max={max}
            />
          );
        },
        align: 'left',
        sortable: !isExist ? false : true,
      };
      columns.push(column);
    });

    return columns;
  },
);

// map threshold data for faster lookup
export const thresholdSelector = createSelector(initThresholdSelector, (res) => {
  const thresholdList = res.data.thresholds || [];
  const values = {};

  thresholdList.forEach((row) => {
    values[row.filter.fid || row.filter.key] = row;
  });
  return values;
});

export const warmingUpFiltersSelector = createSelector(dataSelector, (res) =>
  get(res, 'warmup', []).map((row) => ({
    id: row.fid || row.key,
    filter: { name: row.name },
    warmup: true,
  })),
);

// build data from response
export const tableDataSelector = createSelector(
  filtersTableDataSortSelector,
  thresholdSelector,
  thresholdBaselineSelector,
  metricSelector,
  isThresholdDisabledSelector,
  warmingUpFiltersSelector,
  autoSortEnabledSelector,
  (
    res,
    thresholds,
    thresholdBaseline,
    metrics,
    isThresholdDisabled,
    warmupFilters,
    isAutoSortEnabled,
  ) => {
    const { data, sort } = res;

    const result =
      data.map((rowData) => ({
        ...rowData,
        // calculate row level threshold
        threshold: isThresholdDisabled
          ? {}
          : calcRowThreshold({
              rowData,
              metrics,
              thresholds,
              thresholdBaseline,
            }),
      })) || [];

    autoSort(isAutoSortEnabled, result);

    return {
      data: result.concat(warmupFilters),
      sort,
    };
  },
);
